/** News source values — local branch content vs national CPRE / network news. */
export const NEWS_SOURCE = {
  LOCAL: 'local',
  NATIONAL: 'national',
};

export const NEWS_SOURCE_OPTIONS = [
  { id: NEWS_SOURCE.LOCAL, label: 'Local' },
  { id: NEWS_SOURCE.NATIONAL, label: 'National' },
];

/** Resolve article source; falls back to local when field is missing (legacy data). */
export function resolveArticleSource(article) {
  if (article?.source === NEWS_SOURCE.NATIONAL || article?.source === NEWS_SOURCE.LOCAL) {
    return article.source;
  }
  return NEWS_SOURCE.LOCAL;
}

export function filterNewsBySource(articles, source) {
  if (!source || !articles?.length) return articles || [];
  return articles.filter((article) => resolveArticleSource(article) === source);
}

/** Slug for news detail URLs — matches title-based paths used in production */
export function slugifyTitle(title = '') {
  return title
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Home Latest News section — fixed titles and display order (left to right). */
export const HOME_LATEST_NEWS_TITLES = [
  'CPRE National Solar Topic Group',
  'Lincolnshire Pylons Infrastructure Proposal - What You Need to Know',
  'CPRE Centenary National Awards - Nominate Now!',
];

export const HOME_LATEST_NEWS_FALLBACK = [
  {
    id: 'home-solar',
    title: 'CPRE National Solar Topic Group',
    excerpt:
      'CPRE Rutland & Lincolnshire has joined the new CPRE National Solar Topic Group, sharing local experience to strengthen the campaign for rooftop solar over farmland.',
    category: 'campaigns',
    source: 'local',
    image_url: '/images/cpre images for website/33.jpg',
    created_at: new Date().toISOString(),
  },
  {
    id: 'home-pylons',
    title: 'Lincolnshire Pylons Infrastructure Proposal - What You Need to Know',
    excerpt:
      'National Grid proposals include 140km of new pylons from Grimsby to Walpole and two substations at Weston Marsh. CPRE urges underground cabling as the alternative.',
    category: 'planning',
    source: 'local',
    image_url:
      'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?crop=entropy&cs=srgb&fm=jpg&q=85',
    created_at: new Date().toISOString(),
  },
  {
    id: 'home-awards',
    title: 'CPRE Centenary National Awards - Nominate Now!',
    excerpt:
      "Nominate outstanding individuals and projects for CPRE's Centenary National Awards. Deadline: 24 June 2026.",
    category: 'centenary',
    source: 'local',
    image_url:
      'https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85',
    created_at: new Date().toISOString(),
  },
];

export function getHomeLatestNewsArticles(articles) {
  return HOME_LATEST_NEWS_TITLES.map((title) => {
    const match =
      articles.find((article) => article.title === title) ||
      HOME_LATEST_NEWS_FALLBACK.find((article) => article.title === title);
    return match || null;
  }).filter(Boolean);
}

/** Chronological order: newest first (same convention as events). */
export function sortNewsByDateDesc(articles) {
  return [...articles].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

export function getNewsArticlePath(article) {
  if (!article) return '/news';
  const segment = article.slug || article.id || slugifyTitle(article.title);
  return `/news/${encodeURIComponent(segment)}`;
}

export const CPRE_NATIONAL_NEWS_URL = 'https://www.cpre.org.uk/news/';

/** National / network news opens on cpre.org.uk — not the internal article page. */
export function isExternalNewsArticle(article) {
  return resolveArticleSource(article) === NEWS_SOURCE.NATIONAL;
}

export function getExternalNewsUrl(article) {
  if (!isExternalNewsArticle(article)) return null;
  return article?.external_url || CPRE_NATIONAL_NEWS_URL;
}

/** National / network news from Centenary page — used when API is unavailable. */
export const NATIONAL_NEWS_FALLBACK = [
  {
    id: 'national-chelsea',
    title: 'RHS Chelsea Gold Medal',
    excerpt: 'On the Edge campaign won an award at the flower show.',
    content: 'On the Edge campaign won an award at the flower show.',
    author: 'CPRE National',
    category: 'general',
    source: 'national',
    external_url: 'https://www.cpre.org.uk/news/',
    image_url: '/images/cpre images for website/30.jpg',
    created_at: '2026-05-01T00:00:00.000Z',
  },
  {
    id: 'national-awards-judges',
    title: 'Centenary Awards rooftop solar judges',
    excerpt: "Judges announced for CPRE's 100th year awards.",
    content: "Judges announced for CPRE's 100th year awards.",
    author: 'CPRE National',
    category: 'general',
    source: 'national',
    external_url: 'https://www.cpre.org.uk/news/',
    image_url: '/images/cpre images for website/33.jpg',
    created_at: '2026-05-15T00:00:00.000Z',
  },
  {
    id: 'national-planning-surgery',
    title: 'Planning Surgery 29 May',
    excerpt: 'An upcoming event (this replaces the old April event).',
    content: 'An upcoming event (this replaces the old April event).',
    author: 'CPRE National',
    category: 'general',
    source: 'national',
    external_url: 'https://www.cpre.org.uk/news/',
    image_url: '/images/cpre images for website/31.jpg',
    created_at: '2026-05-29T00:00:00.000Z',
  },
  {
    id: 'national-volunteers-week',
    title: "Volunteers' Week 1–7 June",
    excerpt: 'National awareness week coming up.',
    content: 'National awareness week coming up.',
    author: 'CPRE National',
    category: 'general',
    source: 'national',
    external_url: 'https://www.cpre.org.uk/news/',
    image_url: '/images/cpre images for website/27.jpg',
    created_at: '2026-06-01T00:00:00.000Z',
  },
  {
    id: 'national-infrastructure-petition',
    title: 'Stop One Minister Deciding on Major Infrastructure Projects',
    excerpt:
      'Sign the parliamentary petition to stop one minister deciding on major infrastructure projects — including pylons and solar farms affecting Lincolnshire.',
    content:
      'A parliamentary petition calls for an end to the practice of one minister alone deciding on major infrastructure projects without proper democratic scrutiny.\n\nMajor infrastructure decisions affect communities across England — including Lincolnshire, where proposals for overhead pylon lines and large-scale solar farm developments on agricultural land could permanently alter our countryside.\n\nCPRE believes that decisions of this scale should not rest with a single minister. Local communities deserve a say in projects that affect their landscapes, farmland and quality of life.\n\nWhy this matters for Lincolnshire:\n• Proposals for new pylon infrastructure across the county\n• Large solar farm applications on productive agricultural land\n• The loss of countryside character and food-producing land\n\nSign the petition: https://petition.parliament.uk/petitions/768378\n\nWe encourage all supporters to sign and share the petition to help ensure major infrastructure decisions are made with proper accountability and community involvement.',
    author: 'CPRE National',
    category: 'planning',
    source: 'national',
    external_url: 'https://petition.parliament.uk/petitions/768378',
    image_url: '/images/cpre images for website/34.png',
    created_at: '2026-06-12T00:00:00.000Z',
  },
];

export function formatNetworkNewsDate(dateStr) {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.getDate();
  const month = date.toLocaleDateString('en-GB', { month: 'long' });
  const year = date.getFullYear();
  if (day === 1) {
    return `${month} ${year}`;
  }
  return `${day} ${month} ${year}`;
}

export function findNewsArticleByRouteId(articles, routeId) {
  if (!routeId || !articles?.length) return null;
  const decoded = decodeURIComponent(routeId);

  const exactMatch = articles.find(
    (article) =>
      article.id === decoded ||
      article.slug === decoded ||
      slugifyTitle(article.title) === decoded
  );
  if (exactMatch) return exactMatch;

  // Match when list title slug differs slightly from detail/fallback title (e.g. shortened card titles)
  return (
    articles.find((article) => {
      const titleSlug = slugifyTitle(article.title);
      return titleSlug.startsWith(`${decoded}-`) || decoded.startsWith(`${titleSlug}-`);
    }) ?? null
  );
}

export const NEWS_FALLBACK_ARTICLES = [
  {
    id: '1',
    title: 'CPRE Rutland & Lincolnshire Celebrates 100 Years',
    excerpt: 'Celebrating a century of countryside protection in Rutland and Lincolnshire.',
    content:
      'As we mark our centenary year, we reflect on a century of protecting the countryside that makes our region so special. From the rolling hills of the Lincolnshire Wolds to the tranquil waters of Rutland Water, CPRE has been at the forefront of conservation efforts.\n\nOver the past 100 years, we have successfully campaigned against inappropriate development, protected green spaces, and helped maintain the character of our rural communities.',
    author: 'CPRE Communications Team',
    category: 'centenary',
    source: 'local',
    image_url: '/images/cpre images for website/19.jpg',
    created_at: new Date().toISOString(),
  },
  {
    id: '2',
    title: 'Major Planning Application Threatens Lincolnshire Farmland',
    excerpt:
      'CPRE urges residents to object to industrial development on prime farmland.',
    content:
      'A significant planning application has been submitted that could see prime agricultural land converted for industrial use near Grantham. CPRE is calling on local residents to voice their concerns and help protect this valuable countryside.',
    author: 'Planning Team',
    category: 'planning',
    source: 'local',
    image_url: '/images/cpre images for website/2.jpg',
    created_at: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    id: '3',
    title: 'Volunteer Tree Planting Day Success',
    excerpt:
      'Over 200 trees planted by community volunteers in centenary celebration.',
    content:
      'Last weekend saw our most successful volunteer event to date, with over 50 participants helping plant 200 native trees at our Centenary Oak project site. The weather was kind, and the community spirit was wonderful to witness.',
    author: 'Events Team',
    category: 'projects',
    source: 'local',
    image_url: '/images/cpre images for website/10.jpg',
    created_at: new Date(Date.now() - 172800000).toISOString(),
  },
  {
    id: 'local-nominate-now',
    title: 'CPRE Centenary National Awards - Nominate Now!',
    excerpt:
      "Nominate outstanding individuals and projects for CPRE's Centenary National Awards. Deadline: 24 June 2026.",
    content:
      "CPRE has launched a prestigious national awards programme to celebrate our centenary year, recognising outstanding individuals, projects, and countryside initiatives that have made a significant impact on protecting and enhancing England's rural landscapes.",
    author: 'CPRE Communications Team',
    category: 'centenary',
    source: 'local',
    image_url:
      'https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85',
    created_at: '2026-03-01T00:00:00.000Z',
  },
  {
    id: 'local-pylons',
    title: 'Lincolnshire Pylons Infrastructure Proposal - What You Need to Know',
    excerpt:
      'National Grid proposals include 140km of new pylons from Grimsby to Walpole and two substations at Weston Marsh. CPRE urges underground cabling as the alternative.',
    content:
      'National Grid proposals to build more pylons across Lincolnshire include constructing 140km of pylons between Grimsby and Walpole in Norfolk, along with two 400kv substations at Weston Marsh.',
    author: 'Planning Team',
    category: 'planning',
    source: 'local',
    image_url:
      'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?crop=entropy&cs=srgb&fm=jpg&q=85',
    created_at: '2026-03-05T00:00:00.000Z',
  },
  {
    id: 'local-lnrs',
    title: 'Local Nature Recovery Strategies',
    excerpt:
      'CPRE Rutland & Lincolnshire has responded to the Lincolnshire LNRS consultation. Find out what Local Nature Recovery Strategies mean for our area.',
    content:
      'England is one of the most nature-depleted countries in the world. Each part of the country must have a Local Nature Recovery Strategy (LNRS). CPRE Rutland and Lincolnshire responded to the Lincolnshire LNRS consultation.',
    author: 'CPRE Rutland & Lincolnshire',
    category: 'nature',
    source: 'local',
    image_url: '/images/cpre images for website/6.jpg',
    created_at: '2026-04-12T00:00:00.000Z',
  },
  {
    id: 'local-land-use',
    title: 'National Land Use Framework',
    excerpt:
      'The Government published its Land Use Framework in March 2026. CPRE welcomes parts of it but warns that land is finite and must be protected for future generations.',
    content:
      'In March 2026 the Government published its long-awaited Land Use Framework for England. CPRE have produced a full report on what this means for countryside protection.',
    author: 'CPRE Rutland & Lincolnshire',
    category: 'planning',
    source: 'local',
    image_url: '/images/cpre images for website/2.jpg',
    created_at: '2026-04-10T00:00:00.000Z',
  },
  {
    id: 'local-solar-topic',
    title: 'CPRE National Solar Topic Group',
    excerpt:
      'CPRE Rutland & Lincolnshire has joined the new CPRE National Solar Topic Group, sharing local experience to strengthen the campaign for rooftop solar over farmland.',
    content:
      'CPRE Rutland and Lincolnshire have appointed a representative to join the new CPRE National topic group for branches tackling mega solar applications in their area.',
    author: 'CPRE Rutland & Lincolnshire',
    category: 'campaigns',
    source: 'local',
    image_url: '/images/cpre images for website/33.jpg',
    created_at: '2026-04-08T00:00:00.000Z',
  },
  {
    id: 'local-awards-launched',
    title: 'CPRE Centenary National Awards Launched',
    excerpt:
      'CPRE Centenary National Awards recognise people and projects helping the countryside thrive. Nominations close 24 June 2026.',
    content:
      'This month CPRE has launched its Centenary National Awards to recognise the people, projects and ideas helping the countryside and its communities thrive. The Awards will highlight practical solutions already happening across England - from community green spaces and countryside access projects to rooftop solar and local changemakers - helping to tell a strong national story about positive change for the countryside.\n\nHow can you help?\n\nThere are two key ways to support the Awards:\n\n1. Nominate local projects or people: We strongly encourage you to submit nominations for projects, community groups or individuals making a difference in your area. Contact [chair@cprerutlandandlincs.uk](mailto:chair@cprerutlandandlincs.uk) for more information on how to enter the awards.\n\n2. Help promote the Awards in your neighbourhood: Please help spread the word through your network of friends and contacts encouraging supporters and local organisations to nominate.\n\nNominations are open until noon on Wednesday 24 June 2026, with winners to be celebrated in October 2026 as part of the CPRE centenary programme.',
    author: 'CPRE Rutland & Lincolnshire',
    category: 'centenary',
    source: 'local',
    image_url: '/images/cpre images for website/15.jpg',
    created_at: '2026-04-15T00:00:00.000Z',
  },
  {
    id: 'local-trustee-vacancies',
    title: 'Lincolnshire Trustee Vacancies',
    excerpt: 'Treasurer and Trustee vacancies open for the Lincolnshire area. Call 07710 328469 if interested.',
    content:
      'The newly enlarged branch is still looking for a new Treasurer and some additional Trustees (particularly with planning experience) from the Lincolnshire area.\n\nIf you are interested in helping protect the Lincolnshire countryside and have relevant experience, we would love to hear from you.\n\nCall 07710 328469 if interested.',
    author: 'CPRE Rutland & Lincolnshire',
    category: 'general',
    source: 'local',
    image_url: '/images/cpre images for website/16.jpg',
    created_at: '2026-04-14T00:00:00.000Z',
  },
];

/** First three articles for home / volunteering previews — same ids as NEWS_FALLBACK_ARTICLES */
export const NEWS_FALLBACK_PREVIEW = NEWS_FALLBACK_ARTICLES.slice(0, 3);
